import { useState } from 'react';

import { withForm } from '@/components/form';
import {
  AddCustomField,
  Button,
  EditableField,
  FieldGrid,
  Icon,
  IconButton,
  Input,
  SectionCard,
  Select,
  type EditableFieldType,
} from '@/components/ui';

import { defaultFormValues } from '../../contactSchema';

interface CustomField {
  name: string;
  type: EditableFieldType;
  value: string;
}

const PHONE_TYPE_OPTIONS = [
  { value: '', label: '—' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'home', label: 'Home' },
  { value: 'work', label: 'Work' },
];

const EMAIL_TYPE_OPTIONS = [
  { value: '', label: '—' },
  { value: 'personal', label: 'Personal' },
  { value: 'work', label: 'Work' },
  { value: 'academic', label: 'Academic' },
];

export const ContactInfoSection = withForm({
  defaultValues: defaultFormValues,
  render: function ContactInfoSection({ form }) {
    const [customFields, setCustomFields] = useState<CustomField[]>([]);

    return (
      <SectionCard title="Contact information" dense>
        <FieldGrid>
          <form.AppField name="contact.primaryEmail">
            {(field) => (
              <EditableField
                label="Primary email"
                required
                type="email"
                value={field.state.value ?? ''}
                display={field.state.value || ''}
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>

          {customFields.map((cf) => (
            <EditableField
              key={cf.name}
              label={cf.name}
              type={cf.type}
              custom
              value={cf.value}
              display={cf.value || '—'}
              onSave={(v) =>
                setCustomFields((prev) => prev.map((f) => (f.name === cf.name ? { ...f, value: v } : f)))
              }
            />
          ))}
        </FieldGrid>

        <form.AppField name="contact.phones">
          {(field) => (
            <RepeaterBlock
              title="Phone numbers"
              icon={<Icon name="phone" size={13} />}
              onAdd={() => form.pushFieldValue('contact.phones', { number: '', type: '', primary: false })}
              empty="No phone numbers"
              count={field.state.value.length}
            >
              {field.state.value.map((_, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 140px 28px',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <form.AppField name={`contact.phones[${i}].number`}>
                    {(sub) => (
                      <Input
                        placeholder="Number"
                        value={sub.state.value ?? ''}
                        onChange={(e) => sub.handleChange(e.target.value)}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name={`contact.phones[${i}].type`}>
                    {(sub) => (
                      <Select
                        options={PHONE_TYPE_OPTIONS}
                        value={sub.state.value ?? ''}
                        onChange={(e) => sub.handleChange(e.target.value as typeof sub.state.value)}
                      />
                    )}
                  </form.AppField>
                  <IconButton
                    icon="trash"
                    iconSize={12}
                    size="sm"
                    label="Remove phone number"
                    variant="danger"
                    onClick={() => field.removeValue(i)}
                  />
                </div>
              ))}
            </RepeaterBlock>
          )}
        </form.AppField>

        <form.AppField name="contact.emails">
          {(field) => (
            <RepeaterBlock
              title="Additional emails"
              icon={<Icon name="mail" size={13} />}
              onAdd={() => form.pushFieldValue('contact.emails', { email: '', type: '' })}
              empty="No additional emails"
              count={field.state.value.length}
            >
              {field.state.value.map((_, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 140px 28px',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <form.AppField name={`contact.emails[${i}].email`}>
                    {(sub) => (
                      <Input
                        placeholder="Email"
                        type="email"
                        value={sub.state.value ?? ''}
                        onChange={(e) => sub.handleChange(e.target.value)}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name={`contact.emails[${i}].type`}>
                    {(sub) => (
                      <Select
                        options={EMAIL_TYPE_OPTIONS}
                        value={sub.state.value ?? ''}
                        onChange={(e) => sub.handleChange(e.target.value as typeof sub.state.value)}
                      />
                    )}
                  </form.AppField>
                  <IconButton
                    icon="trash"
                    iconSize={12}
                    size="sm"
                    label="Remove email"
                    variant="danger"
                    onClick={() => field.removeValue(i)}
                  />
                </div>
              ))}
            </RepeaterBlock>
          )}
        </form.AppField>

        <AddCustomField
          section="Contact"
          onAdd={(name, type) => setCustomFields((prev) => [...prev, { name, type, value: '' }])}
        />
      </SectionCard>
    );
  },
});

function RepeaterBlock({
  title,
  icon,
  count,
  onAdd,
  empty,
  children,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  count: number;
  onAdd: () => void;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ borderTop: '1px solid var(--divider)', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-flex', color: 'var(--text-3)' }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.04 }}>{title}</span>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>· {count}</span>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Icon name="plus" size={12} />}
          onClick={onAdd}
          style={{ marginLeft: 'auto', height: 24, padding: '0 6px', fontSize: 11 }}
        >
          Add
        </Button>
      </div>
      {count === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-4)', fontStyle: 'italic' }}>{empty}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
      )}
    </div>
  );
}
